import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Download, X, Smartphone, Share } from 'lucide-react';
import { toast } from 'sonner';

// Helper function to check install state (can be called synchronously)
const getInitialInstallState = () => {
  if (typeof window === 'undefined') return { isInstalled: false, isIOSPWA: false, isIOS: false };
  
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = window.navigator.standalone === true;
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  
  return {
    isInstalled: isStandalone || isIOSStandalone,
    isIOSPWA: isIOSStandalone,
    isIOS
  };
};

export default function InstallPrompt({ variant = 'banner', primaryColor, customerName, onIOSInstallClick }) {
  const initialState = getInitialInstallState();
  
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [isInstalled, setIsInstalled] = useState(initialState.isInstalled);
  const [isIOS] = useState(initialState.isIOS);
  const [isIOSPWA] = useState(initialState.isIOSPWA);

  useEffect(() => {
    // If already installed, don't set up listeners
    if (isInstalled) return;

    // Listen for install prompt (Android/Desktop Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Only auto-show banner if not dismissed this session
      if (sessionStorage.getItem('installPromptDismissed') !== 'true') {
        setShowBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Also show banner for iOS Safari if not dismissed this session
    const isIOSSafari = isIOS && /Safari/.test(navigator.userAgent) && !/CriOS|Chrome/.test(navigator.userAgent);
    if (isIOSSafari && sessionStorage.getItem('installPromptDismissed') !== 'true') {
      setShowBanner(true);
    }

    // Listen for successful install
    const installHandler = () => {
      setIsInstalled(true);
      setShowBanner(false);
      toast.success('App installed successfully!');
    };
    window.addEventListener('appinstalled', installHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installHandler);
    };
  }, [isInstalled, isIOS]);

  const handleInstall = async () => {
    if (isIOS) {
      // If a custom iOS install handler is provided, use it
      if (onIOSInstallClick) {
        onIOSInstallClick();
        return;
      }
      
      // Show iOS-specific instructions via toast
      toast.info(
        <div className="space-y-2">
          <p className="font-medium">To install on iOS:</p>
          <ol className="text-sm space-y-1 list-decimal list-inside">
            <li>Tap the <Share className="inline w-4 h-4" /> Share button</li>
            <li>Scroll down and tap &quot;Add to Home Screen&quot;</li>
            <li>Tap &quot;Add&quot; to confirm</li>
          </ol>
        </div>,
        { duration: 10000 }
      );
      return;
    }

    if (!deferredPrompt) {
      // Show a clear step-by-step guide dialog instead of a disappearing toast
      setShowGuide(true);
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('Installing app...');
    }
    
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    // Use sessionStorage so the banner reappears next visit (not permanently hidden)
    sessionStorage.setItem('installPromptDismissed', 'true');
  };

  // Don't render anything if already installed
  if (isInstalled) {
    return null;
  }

  // Don't render compact/button variants on iOS Chrome (can't install PWA)
  const isIOSChrome = isIOS && /CriOS|Chrome/.test(navigator.userAgent);
  if ((variant === 'compact' || variant === 'button') && isIOSChrome) {
    return null;
  }

  // Install guide dialog (shown when native prompt is unavailable)
  const InstallGuideDialog = () => (
    <Dialog open={showGuide} onOpenChange={setShowGuide}>
      <DialogContent className="max-w-sm" data-testid="install-guide-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Smartphone className="w-5 h-5" style={{ color: primaryColor || '#2563EB' }} />
            How to Install the App
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isIOS ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">To add to your iPhone home screen:</p>
              {[
                { num: 1, text: 'Tap the Share button at the bottom of Safari (the box with an arrow)' },
                { num: 2, text: 'Scroll down in the menu and tap "Add to Home Screen"' },
                { num: 3, text: 'Tap "Add" in the top right corner' },
              ].map(step => (
                <div key={step.num} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold"
                    style={{ backgroundColor: primaryColor || '#2563EB' }}>
                    {step.num}
                  </span>
                  <p className="text-sm pt-0.5">{step.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">To install on Android using Chrome:</p>
              {[
                { num: 1, text: 'Tap the three-dot menu in the top-right corner of Chrome', icon: '⋮' },
                { num: 2, text: 'Tap "Add to Home screen" or "Install App"', icon: '＋' },
                { num: 3, text: 'Tap "Add" or "Install" to confirm', icon: '✓' },
              ].map(step => (
                <div key={step.num} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full text-white text-xs flex items-center justify-center font-bold"
                    style={{ backgroundColor: primaryColor || '#2563EB' }}>
                    {step.num}
                  </span>
                  <span className="text-xl flex-shrink-0">{step.icon}</span>
                  <p className="text-sm">{step.text}</p>
                </div>
              ))}
              {/* Visual hint showing where the menu is */}
              <div className="rounded-lg border border-dashed border-gray-300 p-3 flex items-center gap-3 bg-gray-50">
                <div className="flex-1 text-xs text-muted-foreground">
                  Look for <strong>⋮</strong> in the top-right corner of your Chrome browser bar
                </div>
                <div className="flex-shrink-0 flex flex-col gap-0.5 items-center justify-center w-8 h-8 rounded bg-white border shadow-sm">
                  <div className="w-1 h-1 rounded-full bg-gray-500" />
                  <div className="w-1 h-1 rounded-full bg-gray-500" />
                  <div className="w-1 h-1 rounded-full bg-gray-500" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Make sure you're using <strong>Chrome</strong> — the option won't appear in other browsers.
              </p>
            </div>
          )}
          <Button
            className="w-full"
            style={{ backgroundColor: primaryColor || '#2563EB' }}
            onClick={() => setShowGuide(false)}
            data-testid="install-guide-close-btn"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Header button variant - always show if not installed
  if (variant === 'button') {
    return (
      <>
        <Button
          size="sm"
          variant="outline"
          onClick={handleInstall}
          className="gap-2"
          style={{ 
            borderColor: primaryColor || '#2563EB',
            color: primaryColor || '#2563EB'
          }}
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Install App</span>
        </Button>
        <InstallGuideDialog />
      </>
    );
  }

  // Compact button variant for inline use
  if (variant === 'compact') {
    // For iOS Safari, show a more prominent button with notification emphasis
    if (isIOS && !isIOSChrome) {
      return (
        <>
          <div className="mb-4 space-y-2">
            <Button
              size="default"
              onClick={handleInstall}
              className="gap-2 w-full text-white font-medium"
              style={{ 
                backgroundColor: primaryColor || '#2563EB',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <Smartphone className="w-4 h-4" />
              Install App for Notifications
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Required on iPhone/iPad to receive push notifications
            </p>
          </div>
          <InstallGuideDialog />
        </>
      );
    }
    
    // Default compact button for other platforms
    return (
      <>
        <div className="mb-4 space-y-1">
          <Button
            size="sm"
            onClick={handleInstall}
            className="gap-2 w-full"
            style={{ 
              backgroundColor: primaryColor || '#2563EB',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <Download className="w-4 h-4" />
            Add to Home Screen
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Open in Chrome, then tap ⋮ → "Add to Home screen"
          </p>
        </div>
        <InstallGuideDialog />
      </>
    );
  }

  // Banner variant - only show if not dismissed
  if (!showBanner) {
    return <InstallGuideDialog />;
  }

  return (
    <>
      <InstallGuideDialog />
    <Card 
      className="fixed bottom-4 left-4 right-4 z-50 shadow-2xl border-2 animate-in slide-in-from-bottom max-w-md mx-auto overflow-hidden" 
      style={{ borderColor: primaryColor || '#2563EB' }}
      data-testid="install-prompt"
    >
      {/* Gradient header strip */}
      <div 
        className="h-1.5" 
        style={{ 
          background: `linear-gradient(90deg, ${primaryColor || '#2563EB'}, ${primaryColor || '#2563EB'}88)` 
        }}
      />
      
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor || '#2563EB'}15` }}
          >
            <Smartphone className="w-6 h-6" style={{ color: primaryColor || '#2563EB' }} />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base mb-1">
              {isIOS ? 'Add to Home Screen' : 'Install App'}
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              {isIOS 
                ? 'Get the full app experience with quick access from your home screen'
                : `Install ${customerName || 'Taptile'} for quick access and a native app experience`
              }
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={handleInstall} 
                className="gap-2"
                style={{ backgroundColor: primaryColor || '#2563EB' }}
                data-testid="install-button"
              >
                <Download className="w-4 h-4" />
                {isIOS ? 'Show Me How' : 'Install Now'}
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={handleDismiss} 
                data-testid="dismiss-button"
              >
                Maybe Later
              </Button>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="h-8 w-8 p-0 flex-shrink-0 rounded-full"
            data-testid="close-button"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
    </>
  );
}

// Export a hook for checking install state
export function useInstallState() {
  const initialState = getInitialInstallState();
  
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(initialState.isInstalled);

  useEffect(() => {
    // If already installed, don't set up listeners
    if (isInstalled) return;

    // Check if iOS Safari (can "install" via Add to Home Screen, but not Chrome)
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isIOSSafari = isIOS && /Safari/.test(navigator.userAgent) && !/CriOS|Chrome/.test(navigator.userAgent);
    if (isIOSSafari) {
      setCanInstall(true);
    }

    // Listen for install prompt availability
    const handler = () => setCanInstall(true);
    window.addEventListener('beforeinstallprompt', handler);
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isInstalled]);

  return { canInstall, isInstalled };
}
